import { bigint, index, integer, pgTable, text } from 'drizzle-orm/pg-core'
import { relations } from 'drizzle-orm'
import { entities } from './entities.schema'
import { receivables } from '../receivables/receivables.schema'
import { createdAt, id, updatedAt } from '../utils/columns'

/**
 * The log of DATEV Belegpakete handed to a tax advisor.
 *
 * Its job is to answer "what has already gone out, and when" before a second export of the same
 * period creates duplicate Belege in DATEV — a failure the Steuerberater discovers weeks later
 * while preparing an Umsatzsteuervoranmeldung, which is why it is worth a table.
 *
 * Re-exporting is allowed and never destructive: a second package over an overlapping period
 * writes a *new* row and leaves the earlier zip in the bucket and downloadable. A package can be
 * lost or superseded by a corrected document, and a user who cannot re-export works around us in
 * a way we cannot see.
 */
export const datevExportPackages = pgTable(
  'datev_export_packages',
  {
    id,
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    /** Inclusive period bounds as `yyyy-MM-dd` in Europe/Berlin — the Belegdatum range exported. */
    periodFrom: text('period_from').notNull(),
    periodTo: text('period_to').notNull(),
    /** Where the zip lives: `public/{entityId}/datev-exports/{packageId}.zip`. */
    bucketKey: text('bucket_key').notNull(),
    documentCount: integer('document_count').notNull(),
    /**
     * Snapshot of the numbers the package was built with. Not redundancy: a package is the
     * historical record of what a third party received, and it must stay attributable after the
     * company changes tax advisors.
     */
    consultantNumber: text('consultant_number').notNull(),
    clientNumber: text('client_number').notNull(),
    /**
     * Deliberately not a foreign key to `entity_users`: the id comes from the caller's access
     * token, and a user row can be removed while the historical record of who exported must not
     * disappear with it.
     */
    createdByEntityUserId: text('created_by_entity_user_id'),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [
      index('idx_datev_export_packages_entity_id').on(table.entityId),
      index('idx_datev_export_packages_entity_id_period').on(
        table.entityId,
        table.periodFrom,
        table.periodTo,
      ),
    ]
  },
)
export type DatevExportPackage = typeof datevExportPackages.$inferSelect
export type DatevExportPackageInsert = typeof datevExportPackages.$inferInsert

/**
 * One row per document in a package.
 *
 * Indexed on `receivableId` so "has this document ever gone out, and in which package" is one
 * lookup. The number and amount are snapshotted as exported: a document's figures are stable once
 * issued, but the row has to stay readable even after the document is later cancelled.
 */
export const datevExportPackageDocuments = pgTable(
  'datev_export_package_documents',
  {
    id,
    packageId: text('package_id')
      .notNull()
      .references(() => datevExportPackages.id),
    receivableId: text('receivable_id')
      .notNull()
      .references(() => receivables.id),
    /** Belegnummer as it stood in the package. */
    documentNumber: text('document_number').notNull(),
    /** Gross amount as exported, in cents, recomputed from the line items rather than stored. */
    grossAmount: bigint('gross_amount', { mode: 'number' }).notNull(),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [
      index('idx_datev_export_package_documents_package_id').on(table.packageId),
      index('idx_datev_export_package_documents_receivable_id').on(table.receivableId),
    ]
  },
)
export type DatevExportPackageDocument = typeof datevExportPackageDocuments.$inferSelect
export type DatevExportPackageDocumentInsert = typeof datevExportPackageDocuments.$inferInsert

export const datevExportPackagesRelations = relations(datevExportPackages, ({ one, many }) => ({
  entity: one(entities, {
    fields: [datevExportPackages.entityId],
    references: [entities.id],
  }),
  documents: many(datevExportPackageDocuments),
}))

export const datevExportPackageDocumentsRelations = relations(
  datevExportPackageDocuments,
  ({ one }) => ({
    package: one(datevExportPackages, {
      fields: [datevExportPackageDocuments.packageId],
      references: [datevExportPackages.id],
    }),
    receivable: one(receivables, {
      fields: [datevExportPackageDocuments.receivableId],
      references: [receivables.id],
    }),
  }),
)
